/**
 * Die Zahlen an den Navigationseinträgen — aus dem Bestand, nicht aus dem
 * Quelltext.
 *
 * Sie standen bis zum 12.08.2026 fest in `entwurf/navigation.ts`: 309 Rollen,
 * 189 Applikationen, 354 Exchange-Einträge, 51 offene Punkte. Übernommen aus
 * der HTML-Vorlage, ohne jeden Bezug zur Datenbank. Aufgefallen ist es an
 * „Exchange 354", wo eine Handvoll Postfächer stehen.
 *
 * Eine Zahl an prominenter Stelle, die niemand nachrechnet, ist schlimmer als
 * keine — sie wird geglaubt. Deshalb hier zwei Regeln:
 *
 * 1. **Nur zählen, wofür es eine Quelle gibt.** Wo keine ist, steht keine
 *    Zahl. `Meine Anträge`, `Änderungsfreigaben`, `Rezertifizierung` und
 *    `SoD-Konflikte` arbeiten heute auf Entwurfswerten; sie bekommen nichts.
 * 2. **Zwei Abrufe, nicht dreizehn.** `/api/uebersicht` liefert die
 *    Kennzahlen des Mandanten, `/api/assets/_werte?feld=assetTyp` die
 *    Verteilung über die Assetarten. Beide zählt der Server über den
 *    **ganzen** Bestand; im Browser zu zählen hieße, über die höchstens 200
 *    Zeilen eines Abrufs zu zählen.
 */

import { useMemo } from 'react';
import { useUebersicht, useWerte } from './haken';

/**
 * Welche Assetarten hinter einem Navigationseintrag stehen.
 *
 * Dieselbe Zuordnung wie in `seiten/Assets.tsx`. Sie steht hier ein zweites
 * Mal, weil `Assets.tsx` sie in einer Ansichtskonfiguration mit Spalten,
 * Reitern und Formularen führt — die hier zu importieren hieße, die halbe
 * Ansicht in die Navigation zu ziehen. Weichen beide voneinander ab, zählt
 * die Navigation etwas anderes, als die Seite zeigt; deshalb steht es
 * ausdrücklich hier und nicht verstreut.
 */
const ARTEN_JE_PFAD: Record<string, string[]> = {
  /* Mobile Apps stehen seit dem 07.10.2026 bei den Applikationen (HOWOGE,
     Punkt 18) — der Zaehler zaehlt, was die Seite zeigt. */
  '/assets/applikationen': ['Applikation', 'MobileApp'],
  '/assets/fileserver': ['Fileserver'],
  '/assets/exchange': ['Postfach', 'Verteiler'],
  '/assets/m365': ['M365'],
  '/assets/hardware': ['Hardware'],
};

/**
 * Zahlen je Navigationspfad. Fehlt ein Pfad, steht dort keine Zahl.
 *
 * Bewusst kein `?? 0`: eine Null ist eine Aussage über den Bestand („hier ist
 * nichts"), und solange nichts geladen ist, wäre sie falsch. Kein Eintrag
 * heißt „keine Angabe", und die Seitenleiste zeigt dann nichts.
 */
export function useNavigationszaehler(mandant: string): Record<string, number> {
  const uebersicht = useUebersicht(mandant);
  const assetarten = useWerte('assets', mandant, 'assetTyp', {});

  return useMemo(() => {
    const zahlen: Record<string, number> = {};
    const k = uebersicht.daten?.kennzahlen;

    if (k) {
      if (typeof k['rollen'] === 'number') zahlen['/rollen'] = k['rollen'];
      if (typeof k['zuordnungen'] === 'number') zahlen['/matrix'] = k['zuordnungen'];
      if (typeof k['gruppen'] === 'number') zahlen['/assets/gruppen'] = k['gruppen'];
      if (typeof k['personen'] === 'number') zahlen['/personen'] = k['personen'];
      if (typeof k['organisationseinheiten'] === 'number') {
        zahlen['/organisation'] = k['organisationseinheiten'];
      }
      if (typeof k['offenePunkte'] === 'number') zahlen['/offene-punkte'] = k['offenePunkte'];
    }

    const jeArt = new Map(
      (assetarten.daten?.werte ?? []).map((w) => [w.wert, w.zahl] as const),
    );
    if (jeArt.size > 0) {
      for (const [pfad, arten] of Object.entries(ARTEN_JE_PFAD)) {
        const summe = arten.reduce((s, art) => s + (jeArt.get(art) ?? 0), 0);
        // Nur eintragen, wenn wenigstens eine der Arten wirklich vorkommt —
        // sonst stuende dort eine Null, obwohl nur nichts geladen ist.
        if (arten.some((art) => jeArt.has(art))) zahlen[pfad] = summe;
      }
    }

    return zahlen;
  }, [uebersicht.daten, assetarten.daten]);
}
